/**
 * §6 Team Tactics panel — modal-style HUD surface.
 *
 * Renders the active team's slot picker, build-up style + defensive
 * approach chip rows, and the six 0–100 sliders (pressing, width, depth,
 * tempo, aggression, line height). All edits flow back to the parent via
 * `onChange` with a brand-new bundle — this component never mutates the
 * bundle in place; it delegates to `updateSlider` / `setActiveSlot` from
 * `@4cm/core/tactics`.
 *
 * The panel is rendered as a `role="dialog"` so AT readers announce it as
 * a modal context, and Escape closes via `onClose`.
 */
import { useEffect, useMemo } from 'react';
import type {
  BuildUpStyle,
  DefensiveApproach,
  TeamTacticsBundle,
} from '@4cm/core/tactics/teamTactics';
import {
  BUILD_UP_STYLES,
  DEFENSIVE_APPROACHES,
  SLIDER_FIELDS,
  setActiveSlot,
  updateSlider,
} from '@4cm/core/tactics/teamTactics';
import { HudChip } from './HudChip';
import './TeamTacticsPanel.css';

export interface TeamTacticsPanelProps {
  bundle: TeamTacticsBundle;
  team: 'home' | 'away';
  onChange: (next: TeamTacticsBundle) => void;
  onClose: () => void;
}

// Human-readable enum labels — separate from the enum tuples so the
// raw enum values can stay kebab-case (URL / persistence friendly) while
// the HUD shows title-case copy.
const BUILD_UP_LABELS: Record<BuildUpStyle, string> = {
  'short-pass': 'Short Pass',
  'balanced':  'Balanced',
  'direct':    'Direct',
  'long-ball': 'Long Ball',
};

const DEFENSIVE_LABELS: Record<DefensiveApproach, string> = {
  'low-block':     'Low Block',
  'mid-block':     'Mid Block',
  'high-press':    'High Press',
  'counter-press': 'Counter-Press',
};

export function TeamTacticsPanel({
  bundle,
  team,
  onChange,
  onClose,
}: TeamTacticsPanelProps) {
  const side = bundle[team];

  // Find the currently active slot — fall back to the first slot if a
  // stale activeSlotId snuck through (defensive, matches setActiveSlot's
  // own permissive stance).
  const activeSlot = useMemo(() => {
    return (
      side.slots.find((s) => s.id === side.activeSlotId) ?? side.slots[0]
    );
  }, [side.slots, side.activeSlotId]);

  // Escape-to-close — wired at the document level so it works even if
  // focus is currently on a slider thumb (which would otherwise swallow
  // the event when wired via React's bubble).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!activeSlot) return null;

  const handleSlotPick = (slotId: string) => {
    onChange(setActiveSlot(bundle, team, slotId));
  };

  const handleStylePick = (style: BuildUpStyle) => {
    onChange({
      ...bundle,
      [team]: {
        ...side,
        slots: side.slots.map((s) =>
          s.id === activeSlot.id
            ? { ...s, tactics: { ...s.tactics, buildUpStyle: style } }
            : s,
        ),
      },
    });
  };

  const handleDefencePick = (approach: DefensiveApproach) => {
    onChange({
      ...bundle,
      [team]: {
        ...side,
        slots: side.slots.map((s) =>
          s.id === activeSlot.id
            ? { ...s, tactics: { ...s.tactics, defensiveApproach: approach } }
            : s,
        ),
      },
    });
  };

  const handleSliderChange = (key: string, raw: string) => {
    const value = Number(raw);
    onChange(updateSlider(bundle, team, activeSlot.id, key, value));
  };

  return (
    <div
      className="team-tactics-panel"
      role="dialog"
      aria-modal="true"
      aria-label={`${team === 'home' ? 'Home' : 'Away'} team tactics`}
    >
      <div className="team-tactics-panel__header">
        <h2 className="team-tactics-panel__title">
          {team === 'home' ? 'Home' : 'Away'} Team Tactics
        </h2>
        <button
          type="button"
          className="team-tactics-panel__close"
          onClick={onClose}
          aria-label="Close team tactics"
        >
          ×
        </button>
      </div>

      <div className="team-tactics-panel__section" data-section="slots">
        <span className="team-tactics-panel__label">Slot</span>
        <div className="team-tactics-panel__chips">
          {side.slots.map((s) => (
            <HudChip
              key={s.id}
              team={team}
              active={s.id === activeSlot.id}
              onClick={() => handleSlotPick(s.id)}
            >
              {s.name}
            </HudChip>
          ))}
        </div>
      </div>

      <div className="team-tactics-panel__section" data-section="build-up">
        <span className="team-tactics-panel__label">Build-up</span>
        <div className="team-tactics-panel__chips">
          {BUILD_UP_STYLES.map((style) => (
            <HudChip
              key={style}
              team={team}
              active={activeSlot.tactics.buildUpStyle === style}
              onClick={() => handleStylePick(style)}
            >
              {BUILD_UP_LABELS[style]}
            </HudChip>
          ))}
        </div>
      </div>

      <div className="team-tactics-panel__section" data-section="defence">
        <span className="team-tactics-panel__label">Defence</span>
        <div className="team-tactics-panel__chips">
          {DEFENSIVE_APPROACHES.map((approach) => (
            <HudChip
              key={approach}
              team={team}
              active={activeSlot.tactics.defensiveApproach === approach}
              onClick={() => handleDefencePick(approach)}
            >
              {DEFENSIVE_LABELS[approach]}
            </HudChip>
          ))}
        </div>
      </div>

      <div className="team-tactics-panel__sliders">
        {SLIDER_FIELDS.map((field) => {
          const value = activeSlot.tactics.sliders[field.key] ?? field.default;
          return (
            <label key={field.key} className="team-tactics-panel__slider-row">
              <span className="team-tactics-panel__slider-label">
                {field.label}
              </span>
              <input
                type="range"
                className="team-tactics-panel__slider"
                min={field.min}
                max={field.max}
                step={1}
                value={value}
                aria-label={`${field.label}: ${value}`}
                style={
                  // Track-fill driven by --val (same trick as HudScrubber)
                  // — uses `as` because CSS custom props aren't typed on
                  // CSSProperties without an `any`.
                  { ['--val' as string]: value / 100 } as React.CSSProperties
                }
                onChange={(e) => handleSliderChange(field.key, e.target.value)}
              />
              <span
                className="team-tactics-panel__slider-value"
                aria-hidden="true"
              >
                {value}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
